// web-commands.ts — the PER-APP half of the web shape: maps oriora_outgrown's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (compute_signals) is the app's
// "paid draw" computed in signals.rs — on web it returns a minimal shell for now; a faithful web
// build reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

import { computeSignalsWeb } from "./signals-web";
import type { Child, SizeRecord, InventoryItem } from "./store";

const CHILDREN = "children";
const SIZE_RECORDS = "size_records";
const INVENTORY = "inventory";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Children ──────────────────────────────────────────────────────────────
    list_children: () => db.all(CHILDREN),
    get_child: ({ id }) => db.get(CHILDREN, String(id)),
    create_child: ({ input }) => {
      const i = input as Record<string, unknown>;
      const row = {
        id: uuid(),
        name: String(i.name ?? ""),
        birth_year: (i.birth_year ?? null) as number | null,
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return db.insert(CHILDREN, row);
    },
    update_child: ({ id, patch }) => {
      db.update(CHILDREN, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_child: ({ id }) => {
      db.remove(CHILDREN, String(id));
      return null;
    },

    // ── Size records ──────────────────────────────────────────────────────────
    list_size_records: ({ childId }) => {
      const rows = db.all(SIZE_RECORDS);
      return childId ? rows.filter((r) => (r as { child_id?: string }).child_id === String(childId)) : rows;
    },
    get_size_record: ({ id }) => db.get(SIZE_RECORDS, String(id)),
    create_size_record: ({ input }) => {
      const i = input as Record<string, unknown>;
      const row = {
        id: uuid(),
        child_id: String(i.child_id ?? ""),
        category: String(i.category ?? ""),
        size_label: String(i.size_label ?? ""),
        size_value: (i.size_value ?? null) as number | null,
        brand: String(i.brand ?? ""),
        recorded_on: String(i.recorded_on ?? todayLocal()),
        note: String(i.note ?? ""),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return db.insert(SIZE_RECORDS, row);
    },
    update_size_record: ({ id, patch }) => {
      db.update(SIZE_RECORDS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_size_record: ({ id }) => {
      db.remove(SIZE_RECORDS, String(id));
      return null;
    },

    // ── Inventory ─────────────────────────────────────────────────────────────
    list_inventory: ({ childId }) => {
      const rows = db.all(INVENTORY);
      return childId ? rows.filter((r) => (r as { child_id?: string }).child_id === String(childId)) : rows;
    },
    get_inventory_item: ({ id }) => db.get(INVENTORY, String(id)),
    create_inventory_item: ({ input }) => {
      const i = input as Record<string, unknown>;
      const row = {
        id: uuid(),
        child_id: String(i.child_id ?? ""),
        category: String(i.category ?? ""),
        size_label: String(i.size_label ?? ""),
        size_value: (i.size_value ?? null) as number | null,
        description: String(i.description ?? ""),
        for_season: String(i.for_season ?? ""),
        bought_on: String(i.bought_on ?? ""),
        note: String(i.note ?? ""),
        photo_ref: String(i.photo_ref ?? ""),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return db.insert(INVENTORY, row);
    },
    update_inventory_item: ({ id, patch }) => {
      db.update(INVENTORY, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_inventory_item: ({ id }) => {
      db.remove(INVENTORY, String(id));
      return null;
    },

    // Derived signals — native side computes rich signals in signals.rs. Web shell = valid-but-empty
    // so the UI never crashes; the AI recap feature is degraded until the signal logic is ported to JS.
    // TODO: port derived logic to JS.
    compute_signals: ({ childId, today }) => {
      const child = db.get(CHILDREN, String(childId)) as unknown as Child | null;
      if (!child) throw new Error("child_not_found");
      const records = db.all(SIZE_RECORDS) as unknown as SizeRecord[];
      const inventory = db.all(INVENTORY) as unknown as InventoryItem[];
      const t = String(today ?? "").slice(0, 10);
      const td = /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : new Date().toISOString().slice(0, 10);
      return computeSignalsWeb(child, records, inventory, td);
    },
  };
}
